import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import MapPin from '../../components/MapPin'
import { useAuth } from '../../lib/AuthContext'
import {
  getMisSuscripciones,
  getBarriosActivos,
  cambiarDireccionSuscripcion,
  tienePedidoArmado,
  simularOpcionesPrimeraEntrega,
} from '../../lib/portalClienteApi'
import { geocodificar, validarBarrioConDireccion } from '../../lib/geo'
import { WA_NUMERO } from '../../lib/constantes'


function fmtFechaLarga(iso) {
  if (!iso) return '—'
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado']
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`
}

const DIA_LABEL = {
  lunes: 'lunes', martes: 'martes', miercoles: 'miércoles',
  jueves: 'jueves', viernes: 'viernes', sabado: 'sábado', domingo: 'domingo',
}

export default function CambiarDireccion() {
  const { user } = useAuth()
  const { suscId } = useParams()
  const navigate = useNavigate()

  const [susc, setSusc] = useState(null)
  const [barrios, setBarrios] = useState([])
  const [enCorte, setEnCorte] = useState(false)
  const [cargandoInicial, setCargandoInicial] = useState(true)

  const [form, setForm] = useState({
    barrioId: '',
    direccion: '',
    apto: '',
    tipoVivienda: '',
  })
  const [coords, setCoords] = useState(null)
  const [coordsExactas, setCoordsExactas] = useState(false)
  const [validacionBarrio, setValidacionBarrio] = useState(null)
  const [geocodando, setGeocodando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [err, setErr] = useState(null)
  const [ok, setOk] = useState(false)
  const [proximaInfo, setProximaInfo] = useState(null)  // { fecha, diaNombre, diaCalcestado, fueraDeCorte }

  /* ───── Carga inicial ───── */
  useEffect(() => {
    if (!user?.id || !suscId) return
    let cancelado = false
    Promise.all([
      getMisSuscripciones(user.id),
      getBarriosActivos(),
      tienePedidoArmado(suscId),
    ]).then(([susclist, brrs, corte]) => {
      if (cancelado) return
      const s = (susclist || []).find(x => x.id === suscId)
      setSusc(s || null)
      setBarrios(brrs || [])
      setEnCorte(!!corte)
      if (s) {
        setForm({
          barrioId: s.barrio_obj?.id || '',
          direccion: s.direccion || '',
          apto: s.apto || '',
          tipoVivienda: s.tipo_vivienda || '',
        })
      }
      setCargandoInicial(false)
    })
    return () => { cancelado = true }
  }, [user?.id, suscId])

  const barrioElegido = useMemo(
    () => barrios.find(b => b.id === form.barrioId) || null,
    [barrios, form.barrioId]
  )

  // ¿El día actual del cliente se entrega en el nuevo barrio?
  const diaDisponibleEnNuevo = useMemo(() => {
    if (!barrioElegido || !susc?.dia_entrega) return null  // no aplica
    return (barrioElegido.dias || []).includes(susc.dia_entrega)
  }, [barrioElegido, susc?.dia_entrega])

  /* ───── Calcular próxima entrega al nuevo barrio ─────
   * Respeta corte D-2 y días del barrio. Si el día actual no aplica al
   * nuevo barrio, no hay nada que simular (el cliente va a tener que
   * elegir un nuevo día en Mi Plan después de guardar). */
  useEffect(() => {
    let cancel = false
    if (!form.barrioId || !susc?.dia_entrega || !susc?.semana_mes || diaDisponibleEnNuevo === false) {
      setProximaInfo(null); return
    }
    simularOpcionesPrimeraEntrega({
      barrioId:   form.barrioId,
      diaEntrega: susc.dia_entrega,
      semanaMes:  susc.semana_mes,
    }).then(opts => {
      if (cancel) return
      const primera = opts?.[0]
      if (!primera) { setProximaInfo(null); return }
      setProximaInfo({
        fecha: primera.fechaEntrega,
        diaNombre: primera.nombreDia,
        fueraDeCorte: opts.length > 1,
      })
    }).catch(() => { if (!cancel) setProximaInfo(null) })
    return () => { cancel = true }
  }, [form.barrioId, susc?.dia_entrega, susc?.semana_mes, diaDisponibleEnNuevo])

  /* ───── Geocoding ───── */
  async function geocodear() {
    setErr(null)
    setCoordsExactas(false)
    setValidacionBarrio(null)
    if (!barrioElegido) return
    setGeocodando(true)
    try {
      if (form.direccion.trim()) {
        const r = await geocodificar(form.direccion)
        if (r) {
          setCoords({ lat: r.lat, lng: r.lng })
          setCoordsExactas(true)
          const v = validarBarrioConDireccion(r.barrioDetectado, barrioElegido.nombre, barrios)
          setValidacionBarrio(v)
          return
        }
      }
      const rb = await geocodificar(barrioElegido.nombre)
      if (rb) setCoords({ lat: rb.lat, lng: rb.lng })
    } finally {
      setGeocodando(false)
    }
  }

  function aplicarSugerenciaBarrio() {
    if (!validacionBarrio?.sugerenciaActivo) return
    setForm(f => ({ ...f, barrioId: validacionBarrio.sugerenciaActivo.id }))
    setValidacionBarrio(null)
  }

  /* ───── Submit ───── */
  async function handleGuardar() {
    setErr(null)
    setGuardando(true)
    try {
      if (!form.barrioId) throw new Error('Elegí un barrio')
      if (!form.direccion.trim()) throw new Error('Falta la dirección')
      if (!coords) throw new Error('Verificá la dirección en el mapa antes de continuar')
      if (validacionBarrio && !validacionBarrio.ok) {
        throw new Error('La dirección no coincide con el barrio elegido. Corregila antes de continuar.')
      }
      if (!coordsExactas) throw new Error('Confirmá el pin en el mapa antes de continuar')

      await cambiarDireccionSuscripcion(suscId, {
        barrioId: form.barrioId,
        direccion: form.direccion,
        apto: form.apto,
        tipoVivienda: form.tipoVivienda,
        lat: coords?.lat,
        lng: coords?.lng,
      })
      setOk(true)
      setTimeout(() => navigate('/cliente/mi-plan', { replace: true }), 2200)
    } catch (e) {
      setErr(e?.message || String(e))
      setGuardando(false)
    }
  }

  /* ───── Render ───── */
  if (cargandoInicial) {
    return (
      <ClienteLayout title="Mudarme">
        <div className="cd-wrap"><div className="cd-loading">Cargando…</div></div>
        <style>{cssBase}</style>
      </ClienteLayout>
    )
  }

  if (!susc) {
    return (
      <ClienteLayout title="Mudarme">
        <div className="cd-wrap">
          <div className="cd-card">
            <h1 className="cd-h1">No encontramos esta suscripción</h1>
            <p className="cd-p">Volvé a Mi Plan y elegí la suscripción que querés mudar.</p>
            <Link to="/cliente/mi-plan" className="cd-btn-secondary">← Volver a Mi Plan</Link>
          </div>
        </div>
        <style>{cssBase}</style>
      </ClienteLayout>
    )
  }

  const direccionActualTxt = [
    susc.direccion,
    susc.apto && `apto ${susc.apto}`,
    susc.barrio_obj?.nombre || susc.barrio,
  ].filter(Boolean).join(' · ')

  // Mensaje pre-armado para WhatsApp con corte (D-1: kit ya preparado)
  const waCorteHref = `https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(
    `Hola Limpack! Necesito cambiar mi dirección de entrega, pero mi kit de este mes ya está preparado.\n\n` +
    `📦 Kit: ${susc.kit?.nombre || 'Mi kit'}\n` +
    `📍 Dirección actual: ${direccionActualTxt || '(sin dirección)'}\n` +
    `📍 Dirección nueva: ____________________\n\n` +
    `¿Pueden cambiarlo o lo coordinamos para el próximo ciclo?`
  )}`

  return (
    <ClienteLayout title="Mudarme">
      <div className="cd-wrap">
        <div className="cd-header">
          <Link to="/cliente/mi-plan" className="cd-back">← Mi Plan</Link>
          <h1 className="cd-h1">Cambiar dirección</h1>
          <p className="cd-sub">
            Validamos el barrio con el mapa antes de confirmar. Es lo mismo que cuando contratás una suscripción nueva.
          </p>
        </div>

        {/* ───── Confirmación ───── */}
        {ok && (
          <div className="cd-card cd-ok">
            <div className="cd-ok-icon">✓</div>
            <h2 className="cd-ok-h">Listo, dirección actualizada</h2>
            <p className="cd-ok-p">
              {enCorte && 'El pedido de este ciclo sigue viaje a tu dirección anterior. '}
              {proximaInfo
                ? <>Tu próxima entrega a la <strong>nueva</strong> dirección sale el <strong>{fmtFechaLarga(proximaInfo.fecha)}</strong>.</>
                : (diaDisponibleEnNuevo === false
                    ? 'Acordate de entrar a Mi Plan y elegir un día de entrega disponible en tu nuevo barrio.'
                    : 'A partir del próximo pedido vamos a esa dirección.')
              }
              <br />Te llevamos de vuelta a Mi Plan…
            </p>
          </div>
        )}

        {/* ───── Aviso: pedido en corte (no bloquea, solo informa) ───── */}
        {enCorte && !ok && (
          <div className="cd-card cd-aviso">
            <div className="cd-aviso-icon">📦</div>
            <div className="cd-aviso-body">
              <strong>El pedido de este ciclo ya está armado.</strong>
              <p>
                Este envío sale a tu dirección actual (<strong>{direccionActualTxt}</strong>). La dirección
                nueva que pongas acá se usa desde el <strong>próximo</strong> ciclo en adelante.
              </p>
              <p style={{ marginTop: 8 }}>
                ¿Necesitás cambiar la dirección <em>de este pedido</em>?{' '}
                <a href={waCorteHref} target="_blank" rel="noopener" className="cd-aviso-link">
                  Coordinalo por WhatsApp →
                </a>
              </p>
            </div>
          </div>
        )}

        {/* ───── Form (siempre, salvo en estado OK) ───── */}
        {!ok && (
          <div className="cd-card">
            <div className="cd-actual">
              <div className="cd-actual-l">Dirección actual</div>
              <div className="cd-actual-v">{direccionActualTxt || '—'}</div>
            </div>

            <div className="cd-row-2">
              <Field label="Barrio nuevo" required>
                <select
                  value={form.barrioId}
                  onChange={e => {
                    setForm(f => ({ ...f, barrioId: e.target.value }))
                    setCoords(null); setValidacionBarrio(null); setCoordsExactas(false)
                  }}
                >
                  <option value="">— elegir —</option>
                  {barrios.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
              </Field>
              <Field label="Tipo de vivienda">
                <select
                  value={form.tipoVivienda}
                  onChange={e => setForm(f => ({ ...f, tipoVivienda: e.target.value }))}
                >
                  <option value="">— elegir —</option>
                  <option value="casa">Casa</option>
                  <option value="edificio">Edificio</option>
                  <option value="oficina">Oficina</option>
                </select>
              </Field>
            </div>

            {!form.barrioId && barrios.length > 0 && (
              <div className="cd-info">
                Por ahora cubrimos: {barrios.map(b => b.nombre).join(', ')}.
              </div>
            )}

            <div className="cd-row-2-3">
              <Field label="Calle y número" required>
                <input
                  type="text"
                  value={form.direccion}
                  onChange={e => {
                    setForm(f => ({ ...f, direccion: e.target.value }))
                    setCoords(null); setValidacionBarrio(null); setCoordsExactas(false)
                  }}
                  onBlur={geocodear}
                  placeholder="18 de Julio 1234"
                />
              </Field>
              <Field label="Apto / Piso">
                <input
                  type="text"
                  value={form.apto}
                  onChange={e => setForm(f => ({ ...f, apto: e.target.value }))}
                  placeholder="Apto 406"
                />
              </Field>
            </div>

            <button
              type="button"
              onClick={geocodear}
              disabled={!form.barrioId || geocodando}
              className="cd-btn-secondary"
            >
              {geocodando ? 'Buscando…' : coords ? 'Recargar mapa' : 'Verificar en mapa'}
            </button>

            {/* Validación: barrio detectado vs barrio elegido */}
            {validacionBarrio && !validacionBarrio.ok && (
              <div className={`cd-warn ${validacionBarrio.sinCobertura ? 'is-bloqueo' : 'is-warn'}`}>
                <div className="cd-warn-icon">{validacionBarrio.sinCobertura ? '🚫' : '⚠️'}</div>
                <div className="cd-warn-body">
                  <strong>{validacionBarrio.mensaje}</strong>
                  {validacionBarrio.sinCobertura ? (
                    <>
                      <p>Por ahora no llegamos a esa zona. Mantenemos tu dirección actual.</p>
                      <a
                        href={`https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(
                          `Hola! Me mudé a ${form.direccion}. ¿Hay novedad sobre cuándo llegan a mi zona con Limpack?`
                        )}`}
                        target="_blank" rel="noopener" className="cd-warn-cta"
                      >
                        📲 Avisame cuando lleguen
                      </a>
                    </>
                  ) : validacionBarrio.sugerenciaActivo ? (
                    <>
                      <p>Para continuar, cambiá el barrio elegido o corregí la dirección.</p>
                      <button type="button" className="cd-warn-cta" onClick={aplicarSugerenciaBarrio}>
                        ✓ Cambiar a {validacionBarrio.sugerenciaActivo.nombre}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {coords && !validacionBarrio?.sinCobertura && (
              <>
                <div style={{ marginTop: 12 }}>
                  <MapPin
                    lat={coords.lat}
                    lng={coords.lng}
                    onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }}
                    height={220}
                  />
                </div>
                <label className={`cd-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                  <input
                    type="checkbox"
                    checked={coordsExactas}
                    onChange={e => setCoordsExactas(e.target.checked)}
                  />
                  <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Confirmá el pin antes de seguir'}</span>
                </label>
              </>
            )}

            {/* Aviso fijo: la validación de mapa es obligatoria */}
            {!coords && form.barrioId && form.direccion.trim() && (
              <div className="cd-hint">
                Tocá <em>Verificar en mapa</em> para validar la dirección antes de guardar.
              </div>
            )}

            {/* Aviso si el día actual no se entrega en el nuevo barrio */}
            {diaDisponibleEnNuevo === false && coords && validacionBarrio?.ok !== false && (
              <div className="cd-warn is-warn" style={{ marginTop: 12 }}>
                <div className="cd-warn-icon">📅</div>
                <div className="cd-warn-body">
                  <strong>Tu día actual ({DIA_LABEL[susc.dia_entrega] || susc.dia_entrega}) no se entrega en {barrioElegido?.nombre}.</strong>
                  <p>Podés guardar igual. Después de confirmar, entrá a <em>Mi Plan</em> y elegí un nuevo día — ahí te mostramos los disponibles para {barrioElegido?.nombre}.</p>
                </div>
              </div>
            )}

            {/* Card: próxima entrega a la nueva dirección */}
            {proximaInfo && validacionBarrio?.ok !== false && coords && (
              <div className="cd-proxima">
                <div className="cd-proxima-icon">📦</div>
                <div className="cd-proxima-body">
                  <div className="cd-proxima-label">Tu próxima entrega a esta dirección</div>
                  <div className="cd-proxima-fecha">{fmtFechaLarga(proximaInfo.fecha)}</div>
                  {enCorte && (
                    <div className="cd-proxima-nota">
                      (el pedido de este ciclo sale a la dirección anterior)
                    </div>
                  )}
                  {!enCorte && proximaInfo.fueraDeCorte && (
                    <div className="cd-proxima-nota">
                      A esta hora ya pasamos el corte del día anterior — la primera entrega arranca acá.
                    </div>
                  )}
                </div>
              </div>
            )}

            {err && <div className="cd-err">{err}</div>}

            <div className="cd-actions">
              <Link to="/cliente/mi-plan" className="cd-btn-secondary">Cancelar</Link>
              <button
                type="button"
                className="cd-btn-primary"
                onClick={handleGuardar}
                disabled={
                  guardando
                  || !form.barrioId
                  || !form.direccion.trim()
                  || !coords
                  || (validacionBarrio && !validacionBarrio.ok)
                  || !coordsExactas
                }
              >
                {guardando ? 'Guardando…' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{cssBase}</style>
    </ClienteLayout>
  )
}

/* ───── Helpers de UI ───── */
function Field({ label, required, children }) {
  return (
    <label className="cd-field">
      <span className="cd-field-l">{label}{required && <em>*</em>}</span>
      {children}
    </label>
  )
}

const cssBase = `
/* Colores hardcoded (no CSS vars) para evitar invisibilidad cuando layout es dark */
.cd-wrap { max-width: 720px; margin: 0 auto; padding: 16px 16px 32px; --cd-ink: #1A3461; --cd-muted: #64748B; --cd-line: #E2E8F0; --cd-line2: #F1F5F9; --cd-teal: #00A8B5 }
.cd-loading { padding: 40px; text-align: center; color: var(--cd-muted) }
.cd-header { margin-bottom: 16px }
.cd-back { font-size: 13px; color: #fff; text-decoration: none; font-weight: 700; opacity: 0.85 }
.cd-back:hover { opacity: 1 }
.cd-h1 { font-size: 24px; font-weight: 900; color: #fff; margin: 8px 0 4px; letter-spacing: -.02em }
.cd-sub { font-size: 13px; color: rgba(255,255,255,0.75); line-height: 1.5 }
.cd-card { background: #fff; border: 1px solid var(--cd-line); border-radius: 16px; padding: 18px }
.cd-card .cd-back { color: var(--cd-muted) }
.cd-card .cd-back:hover { color: var(--cd-ink) }

.cd-actual { padding: 12px 14px; background: #F1F5F9; border-radius: 10px; margin-bottom: 16px }
.cd-actual-l { font-size: 11px; color: var(--cd-muted); font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.cd-actual-v { font-size: 14px; font-weight: 700; color: var(--cd-ink); margin-top: 4px }

.cd-row-2 { display: grid; grid-template-columns: 1fr; gap: 12px; margin-bottom: 12px }
.cd-row-2-3 { display: grid; grid-template-columns: 1fr; gap: 12px; margin-bottom: 12px }
@media (min-width: 600px) {
  .cd-row-2 { grid-template-columns: 1fr 1fr }
  .cd-row-2-3 { grid-template-columns: 2fr 1fr }
}

.cd-field { display: flex; flex-direction: column; gap: 6px }
.cd-field-l { font-size: 12px; font-weight: 700; color: var(--cd-ink) }
.cd-field-l em { color: #ef4444; font-style: normal; margin-left: 2px }
.cd-field input, .cd-field select {
  padding: 10px 12px; border: 1.5px solid var(--cd-line); border-radius: 10px;
  font-size: 14px; font-family: inherit; color: var(--cd-ink);
  background: #fff; outline: none; transition: border-color .15s;
}
.cd-field input::placeholder { color: #94A3B8 }
.cd-field input:focus, .cd-field select:focus { border-color: var(--cd-teal); box-shadow: 0 0 0 3px rgba(0,168,181,0.15) }

.cd-info { font-size: 12px; color: var(--cd-muted); margin-bottom: 12px; padding: 10px 12px; background: #F1F5F9; border-radius: 8px; line-height: 1.5 }
.cd-hint { font-size: 12px; color: #92400e; margin: 10px 0; padding: 10px 12px; background: #fffbeb; border: 1px dashed #fcd34d; border-radius: 8px }
.cd-hint em { font-style: normal; font-weight: 800 }

.cd-warn { margin-top: 12px; padding: 14px; border-radius: 10px; display: flex; align-items: flex-start; gap: 10px }
.cd-warn.is-warn { background: #fef3c7; border: 1.5px solid #fcd34d; color: #78350f }
.cd-warn.is-bloqueo { background: #fef2f2; border: 1.5px solid #fca5a5; color: #7f1d1d }
.cd-warn-icon { font-size: 22px; line-height: 1; flex-shrink: 0 }
.cd-warn-body { flex: 1 }
.cd-warn-body strong { display: block; font-size: 14px; margin-bottom: 6px }
.cd-warn-body p { margin: 0 0 10px; line-height: 1.45; font-size: 13px }
.cd-warn-cta {
  display: inline-block; padding: 8px 14px; background: #fff; border-radius: 8px;
  font-size: 12px; font-weight: 800; text-decoration: none; color: inherit; border: 1.5px solid currentColor;
  cursor: pointer; font-family: inherit;
}

.cd-confirm-pin {
  display: flex; align-items: center; gap: 10px; margin-top: 10px;
  padding: 10px 12px; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 700;
}
.cd-confirm-pin.ok { background: #ecfdf5; color: #065f46; border: 1.5px solid #86efac }
.cd-confirm-pin.pend { background: #fef3c7; color: #78350f; border: 1.5px solid #fcd34d }
.cd-confirm-pin input { width: 18px; height: 18px; accent-color: var(--cd-teal) }

.cd-err {
  margin-top: 12px; padding: 10px 12px; background: #fef2f2;
  border: 1px solid #fca5a5; border-radius: 8px; color: #7f1d1d; font-size: 13px;
}

.cd-actions { display: flex; gap: 10px; margin-top: 18px; justify-content: flex-end; flex-wrap: wrap }
.cd-btn-secondary {
  padding: 10px 18px; background: #fff; border: 1.5px solid var(--cd-line);
  border-radius: 10px; font-size: 14px; font-weight: 700; color: var(--cd-ink);
  text-decoration: none; cursor: pointer; font-family: inherit; display: inline-block;
}
.cd-btn-secondary:hover { background: var(--cd-line2) }
.cd-btn-primary {
  padding: 10px 20px; background: var(--cd-teal); border: 0; color: #fff;
  border-radius: 10px; font-size: 14px; font-weight: 800; cursor: pointer; font-family: inherit;
  transition: opacity .15s;
}
.cd-btn-primary:hover:not(:disabled) { opacity: .9 }
.cd-btn-primary:disabled { opacity: .4; cursor: not-allowed }

/* Card: próxima entrega calculada al nuevo barrio */
.cd-proxima {
  margin-top: 14px; padding: 14px 16px;
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 1.5px solid #86efac; border-radius: 12px;
  display: flex; align-items: center; gap: 14px;
}
.cd-proxima-icon { font-size: 32px; flex-shrink: 0 }
.cd-proxima-body { flex: 1 }
.cd-proxima-label { font-size: 11px; font-weight: 800; color: #065f46; text-transform: uppercase; letter-spacing: .05em }
.cd-proxima-fecha { font-size: 16px; font-weight: 900; color: #065f46; margin-top: 4px; text-transform: capitalize }
.cd-proxima-nota { font-size: 11.5px; color: #047857; margin-top: 6px; line-height: 1.4 }

/* Banner informativo: pedido en corte (no bloquea, va arriba del form) */
.cd-aviso {
  background: #fffbeb; border: 1.5px solid #fcd34d;
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px; margin-bottom: 14px;
}
.cd-aviso-icon { font-size: 28px; line-height: 1; flex-shrink: 0 }
.cd-aviso-body { flex: 1; color: #78350f; line-height: 1.5 }
.cd-aviso-body strong { display: block; font-size: 14px; color: #92400e; margin-bottom: 6px }
.cd-aviso-body p { margin: 0; font-size: 13px }
.cd-aviso-body p strong { display: inline; margin: 0; font-size: inherit }
.cd-aviso-link { color: #92400e; font-weight: 800; text-decoration: underline; white-space: nowrap }
.cd-aviso-link:hover { color: #78350f }

/* Confirmación OK */
.cd-ok { text-align: center; padding: 32px 20px }
.cd-ok-icon {
  width: 56px; height: 56px; border-radius: 50%; background: #16a34a; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 28px; font-weight: 900; margin-bottom: 12px;
}
.cd-ok-h { font-size: 18px; font-weight: 900; color: var(--cd-ink); margin-bottom: 8px }
.cd-ok-p { font-size: 13px; color: var(--cd-muted) }

@media (min-width: 900px) {
  .cd-wrap { padding: 28px 32px }
  .cd-h1 { font-size: 28px }
}
`
